// Enhanced SEO utilities to fix Google Search Console issues

export const enhanceImageSchema = (imageUrl: string, altText?: string) => {
  return {
    '@type': 'ImageObject',
    url: imageUrl,
    creditText: 'Carolina Trophy Roofs',
    license: 'https://creativecommons.org/licenses/by/4.0/',
    acquireLicensePage: 'https://www.carolinatrophyroofs.com/contact',
    copyrightNotice: '© Carolina Trophy Roofs. All rights reserved.',
    ...(altText && { description: altText })
  };
};

export const createCompliantBreadcrumbSchema = (breadcrumbs: Array<{ name: string; url: string }>) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: {
        '@type': 'WebPage',
        '@id': crumb.url,
        name: crumb.name
      }
    }))
  };
};

export const createOrganizationSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'RoofingContractor',
    name: 'Carolina Trophy Roofs',
    url: 'https://www.carolinatrophyroofs.com',
    logo: enhanceImageSchema('https://www.carolinatrophyroofs.com/logo.png', 'Carolina Trophy Roofs Logo'),
    description: 'Professional roofing contractor serving Eastern North Carolina with expert roof repair, replacement, and installation services.',
    telephone: '(252) 302-2003',
    email: 'info@carolinatrophyroofs.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '123 Roofing Way',
      addressLocality: 'Rocky Mount',
      addressRegion: 'NC',
      postalCode: '27801',
      addressCountry: 'US'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 35.9382,
      longitude: -77.7905
    },
    openingHours: [
      'Mo-Fr 08:00-18:00',
      'Sa 09:00-16:00'
    ],
    priceRange: '$$',
    serviceArea: {
      '@type': 'State',
      name: 'Eastern North Carolina'
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '150'
    },
    sameAs: [
      'https://www.facebook.com/carolinatrophyroofs',
      'https://www.instagram.com/carolinatrophyroofs/',
      'https://www.angi.com/companylist/us/nc/rocky-mount/carolina-trophy-roofs-reviews-10015537.htm',
      'https://www.bbb.org/us/nc/rocky-mount/profile/roofing-contractors/carolina-trophy-roofs-llc-0593-90328252'
    ]
  };
};

export const validateAndFixSchema = (schema: any): any => {
  // Fix common schema issues
  if (schema['@type'] === 'BreadcrumbList' && schema.itemListElement) {
    // Ensure all breadcrumb items have proper position and structure
    schema.itemListElement = schema.itemListElement.map((item: any, index: number) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: typeof item.item === 'string' ? {
        '@type': 'WebPage',
        '@id': item.item,
        name: item.name
      } : item.item
    }));
  }

  // Fix ImageObject schemas
  if (schema['@type'] === 'ImageObject' && !schema.creditText) {
    return enhanceImageSchema(schema.url, schema.description);
  }

  // Recursively fix nested schemas
  Object.keys(schema).forEach(key => {
    if (typeof schema[key] === 'object' && schema[key] !== null) {
      if (Array.isArray(schema[key])) {
        schema[key] = schema[key].map((item: any) => 
          typeof item === 'object' ? validateAndFixSchema(item) : item
        );
      } else {
        schema[key] = validateAndFixSchema(schema[key]);
      }
    }
  });

  return schema;
};

export const injectValidatedSchema = (schema: any) => {
  if (typeof window === 'undefined') return;
  
  // Validate and fix the schema before injection
  const validatedSchema = validateAndFixSchema(schema);
  
  // Remove existing schema script if it exists
  const existingScript = document.querySelector('script[type="application/ld+json"]');
  if (existingScript) {
    existingScript.remove();
  }
  
  // Create and inject new schema script
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(validatedSchema, null, 2);
  document.head.appendChild(script);
};

export const addSEOMetaTags = (options: {
  title: string;
  description: string;
  canonicalUrl: string;
  keywords?: string[];
  ogImage?: string;
  noIndex?: boolean;
}) => {
  const { title, description, canonicalUrl, keywords = [], ogImage, noIndex = false } = options;
  
  // Update title
  document.title = title;
  
  // Update meta description
  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    document.head.appendChild(metaDescription);
  }
  metaDescription.setAttribute('content', description);
  
  // Update canonical URL
  let canonicalLink = document.querySelector('link[rel="canonical"]');
  if (!canonicalLink) {
    canonicalLink = document.createElement('link');
    canonicalLink.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalLink);
  }
  canonicalLink.setAttribute('href', canonicalUrl);
  
  // Update robots meta
  let robotsTag = document.querySelector('meta[name="robots"]');
  if (!robotsTag) {
    robotsTag = document.createElement('meta');
    robotsTag.setAttribute('name', 'robots');
    document.head.appendChild(robotsTag);
  }
  robotsTag.setAttribute('content', noIndex ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
  
  // Add keywords if provided
  if (keywords.length > 0) {
    let keywordsTag = document.querySelector('meta[name="keywords"]');
    if (!keywordsTag) {
      keywordsTag = document.createElement('meta');
      keywordsTag.setAttribute('name', 'keywords');
      document.head.appendChild(keywordsTag);
    }
    keywordsTag.setAttribute('content', keywords.join(', '));
  }
  
  // Add Open Graph tags
  const ogTags = [
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:type', content: 'website' },
    ...(ogImage ? [{ property: 'og:image', content: ogImage }] : [])
  ];
  
  ogTags.forEach(tag => {
    let ogTag = document.querySelector(`meta[property="${tag.property}"]`);
    if (!ogTag) {
      ogTag = document.createElement('meta');
      ogTag.setAttribute('property', tag.property);
      document.head.appendChild(ogTag);
    }
    ogTag.setAttribute('content', tag.content);
  });
};